import { List, Map, Set } from "immutable";

import { itemSelectors } from "../../modules/items";
import { immutableRecords } from "../../types";

// TODO : Later we may decide to keep attachments?
const eraseObject = {
  references: List(),
  inverseReferences: List(),
  blockingDependencies: List(),
  blockedByDependencies: List(),
  dateConstraints: List(),
  attachments: List()
};

const cleanSuperAndChildren = (item, retainIds) =>
  item
    .update("super", superId => (superId && retainIds.has(superId) ? superId : null))
    .update("children", children => (children ? children.filter(cId => retainIds.has(cId)) : List()));

const getItemsByIdToRetain = (itemsById, retainIds) =>
  itemsById.filter((i, id) => retainIds.has(id)).map(item => cleanSuperAndChildren(item.merge(eraseObject), retainIds));

const getItemsToRetain = (items, retainIds) =>
  items
    .update("byId", itemsById => getItemsByIdToRetain(itemsById, retainIds))
    .update("allIds", allIds => allIds.filter(id => retainIds.has(id)))
    .update("allIdsForType", allIdsForType => allIdsForType.map(idList => idList.filter(id => retainIds.has(id))));

const getSuperAndChildrenToRetain = (superAndChildrenById, retainIds) => {
  return superAndChildrenById.filter((obj, id) => retainIds.has(id)).map(obj => cleanSuperAndChildren(obj, retainIds));
};

// We can keep any filter that does not refer to an item that doesn't exist!
const isFilterLegal = (filter, retainItemIds, retainTagIds) => {
  // Do not allow tag filtering to non-existent tags
  if (filter.tags.terms.find(t => !retainTagIds.has(t))) {
    return false;
  }
  // Do not allow super filtering to non-existent items
  if (filter.super.terms.find(t => !retainItemIds.has(t))) {
    return false;
  }
  // Do not allow relationship filtering to non-existent relationships
  if (filter.relationshipsByType.find(listPart => listPart.terms.find(t => !retainItemIds.has(t)))) {
    return false;
  }
  // Do not allow relationship filtering to non-existent relationships
  if (filter.relationshipsByReference.find(listPart => listPart.terms.find(t => !retainItemIds.has(t)))) {
    return false;
  }
  // Do not allow any narrative filtering based on actual items
  if (filter.narrative.from || filter.narrative.to) {
    return false;
  }
  // Otherwise, it is okay
  return true;
};

const getFiltersToRetain = (filters, retainIds) =>
  filters
    .update("byId", filtersById => filtersById.filter((f, id) => retainIds.has(id)))
    .update("allIds", allIds => allIds.filter(id => retainIds.has(id)));

const getFilterDataToRetain = (filters, savedFilters, retainItemIds, retainTagIds) => {
  // Work out which filters we can retain
  const legalFilterIds = filters.byId
    .filter(f => isFilterLegal(f, retainItemIds, retainTagIds))
    .map((f, id) => id)
    .toList()
    .toSet();
  const savedFilterIds = savedFilters
    .map((name, id) => id)
    .toList()
    .toSet();
  const retainFilterIds = legalFilterIds.intersect(savedFilterIds);
  return {
    filters: getFiltersToRetain(filters, retainFilterIds),
    savedFilters: savedFilters.filter((name, id) => retainFilterIds.has(id))
  };
};

const getDataToSave = (state, retainedTypes, retainCalendarMarkers, retainTimelineRange, retainSavedFilters) => {
  const retainIds = itemSelectors.getItemIdsOfTypes(state, retainedTypes).toSet();
  // For the TimelineDataRecord, we want:
  // 1) Items matching the IDs
  // 2) Items
  const items = getItemsToRetain(state.data.items, retainIds);
  const allTagIds = Set(
    items.byId.reduce((result, item) => {
      result.push(...item.tags.toArray());
      return result;
    }, [])
  );

  let data = {
    items: getItemsToRetain(state.data.items, retainIds),
    superAndChildrenById: getSuperAndChildrenToRetain(state.data.superAndChildrenById, retainIds),
    sequence: state.data.sequence.filter(id => retainIds.has(id)),
    tags: state.data.tags.filter((label, id) => allTagIds.has(id))
  };

  if (retainCalendarMarkers) {
    data.calendarMarkers = state.data.calendarMarkers;
  }
  if (retainTimelineRange) {
    data.timelineRange = state.data.timelineRange;
  }
  if (retainSavedFilters) {
    const { filters, savedFilters } = getFilterDataToRetain(
      state.data.filters,
      state.data.savedFilters,
      retainIds,
      allTagIds
    );
    data.filters = filters;
    data.savedFilters = savedFilters;
  }

  return Map(data);
};

const getUpdatedDefinitions = (definitions, relationshipVisibleColumnIds) =>
  definitions.updateIn(["types", "byId"], typesById =>
    typesById.map(type => type.set("columnIsVisible", relationshipVisibleColumnIds.includes(type.id)))
  );

const getTemplateToSave = (
  state,
  retainedTypes,
  retainCalendarMarkers,
  retainTimelineRange,
  retainSavedFilters,
  relationshipVisibleColumnIds,
  name,
  creator,
  category,
  icon,
  description
) => {
  // Fill in the templateDetails
  const templateDetails = immutableRecords.TemplateDetailsRecord({
    name,
    creator,
    category,
    icon,
    description
  });

  // Grab the definitions directly
  const definitions = getUpdatedDefinitions(state.definitions, relationshipVisibleColumnIds);

  // Create a modified version of the TimelineData keeping only the relevant IDs
  const data = getDataToSave(state, retainedTypes, retainCalendarMarkers, retainTimelineRange, retainSavedFilters);

  return Map({
    data,
    definitions,
    templateDetails
  }).toJS();
};

export default {
  getTemplateToSave
};
